import { useQuery } from "@tanstack/react-query";
import { qualityChecksApi } from "@/app/lib/api/qualityChecks";
import { QualityCheck } from "@/app/lib/supabase/types";
import { useAppStore } from "@/app/lib/stores/useAppStore";

const QUALITY_CHECK_STALE_TIME = 2 * 60 * 1000; // 2 minutes
const QUALITY_CHECK_GC_TIME = 30 * 60 * 1000; // 30 minutes

export const qualityCheckQueryKeys = {
  all: ["qualityChecks"] as const,
  byCompany: (companyId: string, limit?: number) =>
    [...qualityCheckQueryKeys.all, "company", companyId, { limit }] as const,
  byProject: (projectId: string, limit?: number) =>
    [...qualityCheckQueryKeys.all, "project", projectId, { limit }] as const,
  byProductionStage: (productionStageId: string, limit?: number) =>
    [...qualityCheckQueryKeys.all, "productionStage", productionStageId, { limit }] as const,
  byChecklistItem: (productionStageId: string, checklistItemId: string, limit?: number) =>
    [...qualityCheckQueryKeys.all, "checklistItem", productionStageId, checklistItemId, { limit }] as const,
  detail: (qualityCheckId: string) =>
    [...qualityCheckQueryKeys.all, "detail", qualityCheckId] as const,
  stats: (filters: {
    companyId?: string;
    projectId?: string;
    productionStageId?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => [...qualityCheckQueryKeys.all, "stats", filters] as const,
};

// Quality Check hooks
export function useQualityChecks(limit?: number) {
  const { selectedCompanyId } = useAppStore();

  return useQuery<QualityCheck[]>({
    queryKey: qualityCheckQueryKeys.byCompany(selectedCompanyId || "", limit),
    queryFn: () => {
      if (!selectedCompanyId) throw new Error("No company selected");
      return qualityChecksApi.getQualityChecks(selectedCompanyId, limit);
    },
    enabled: !!selectedCompanyId,
    staleTime: QUALITY_CHECK_STALE_TIME,
    gcTime: QUALITY_CHECK_GC_TIME,
  });
}

export function useQualityChecksByProject(projectId: string | undefined, limit?: number) {
  return useQuery<QualityCheck[]>({
    queryKey: qualityCheckQueryKeys.byProject(projectId || "", limit),
    queryFn: () => {
      if (!projectId) throw new Error("No project ID provided");
      return qualityChecksApi.getQualityChecksByProject(projectId, limit);
    },
    enabled: !!projectId,
    staleTime: QUALITY_CHECK_STALE_TIME,
    gcTime: QUALITY_CHECK_GC_TIME,
  });
}

export function useQualityChecksByProductionStage(productionStageId: string | undefined, limit?: number) {
  return useQuery<QualityCheck[]>({
    queryKey: qualityCheckQueryKeys.byProductionStage(productionStageId || "", limit),
    queryFn: () => {
      if (!productionStageId) throw new Error("No production stage ID provided");
      return qualityChecksApi.getQualityChecksByProductionStage(productionStageId, limit);
    },
    enabled: !!productionStageId,
    staleTime: QUALITY_CHECK_STALE_TIME,
    gcTime: QUALITY_CHECK_GC_TIME,
  });
}

export function useQualityCheck(qualityCheckId: string | undefined) {
  return useQuery<QualityCheck>({
    queryKey: qualityCheckQueryKeys.detail(qualityCheckId || ""),
    queryFn: () => {
      if (!qualityCheckId) throw new Error("No quality check ID provided");
      return qualityChecksApi.getQualityCheck(qualityCheckId);
    },
    enabled: !!qualityCheckId,
    staleTime: QUALITY_CHECK_STALE_TIME,
    gcTime: QUALITY_CHECK_GC_TIME,
  });
}

// Quality check statistics
export function useQualityCheckStats(filters: {
  companyId?: string;
  projectId?: string;
  productionStageId?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const { selectedCompanyId } = useAppStore();

  // Use company filter from store if not provided
  const finalFilters = {
    ...filters,
    companyId: (filters.companyId ?? selectedCompanyId) ?? undefined,
  };

  return useQuery<{
    total: number;
    passed: number;
    failed: number;
    na: number;
    passRate: number;
  }>({
    queryKey: qualityCheckQueryKeys.stats(finalFilters),
    queryFn: () => qualityChecksApi.getQualityCheckStats(finalFilters),
    enabled: !!finalFilters.companyId,
    staleTime: QUALITY_CHECK_STALE_TIME,
    gcTime: QUALITY_CHECK_GC_TIME,
  });
}